import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Archive,
  ArrowDown,
  ArrowUp,
  BookOpen,
  ChevronDown,
  ChevronRight,
  Clock,
  Database,
  FolderPlus,
  Home,
  Import,
  Lock,
  PanelLeftClose,
  PanelLeftOpen,
  Pin,
  Plus,
  Settings2,
  Share2,
  Users,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useSession } from '@/stores/session';
import { cn } from '@/lib/utils';
import { useSyncCollection } from '@/features/knowledge_base';
import { collectionsApi } from '../api';
import {
  collectionsHooks,
  pagesHooks,
  stickiesHooks,
  useCollectionAction,
  useReorderCollections,
  useWikiHome,
  useWikiPage,
} from '../queries';
import type { WikiCollection, WikiPage, WikiRecent, WikiSticky } from '../types';
import { WikiPageEditor } from '../components/WikiPageEditor';
import { WikiPageTree } from '../components/WikiPageTree';
import { CollectionDialog } from '../components/CollectionDialog';
import { ConfluenceImportDialog } from '@/features/imports';

function rows<T>(raw: { results: T[] } | T[] | undefined): T[] {
  return (Array.isArray(raw) ? raw : (raw?.results ?? [])) as T[];
}

type Section =
  | { kind: 'home' }
  | { kind: 'collection'; id: number }
  | { kind: 'shared' }
  | { kind: 'private' }
  | { kind: 'archived' };

/** Round-trip the active section through the `?section=` param (home = absent). */
function serializeSection(s: Section): string {
  if (s.kind === 'home') return '';
  return s.kind === 'collection' ? `collection:${s.id}` : s.kind;
}
function parseSection(raw: string | null): Section {
  if (!raw) return { kind: 'home' };
  if (raw.startsWith('collection:')) {
    const id = Number(raw.slice('collection:'.length));
    return Number.isFinite(id) ? { kind: 'collection', id } : { kind: 'home' };
  }
  if (raw === 'shared' || raw === 'private' || raw === 'archived') return { kind: raw };
  return { kind: 'home' };
}

const ADMIN_ROLES = new Set(['super_admin', 'founder']);

export function WikiHomePage() {
  const role = useSession((s) => s.role);
  const canCreateCollection = role != null && ADMIN_ROLES.has(role);

  const collectionsQuery = collectionsHooks.useList({ is_archived: false });
  const collections = useMemo(
    () => rows<WikiCollection>(collectionsQuery.data).sort((a, b) => a.order - b.order),
    [collectionsQuery.data],
  );
  const reorderCollections = useReorderCollections();
  const moveCollection = (index: number, dir: 'up' | 'down') => {
    const target = index + (dir === 'up' ? -1 : 1);
    if (target < 0 || target >= collections.length) return;
    const ids = collections.map((c) => c.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorderCollections.mutate(ids);
  };

  // Section + focused page live in the URL (?section= / ?page=) so they survive
  // reload and the browser Back/Forward + macOS back-swipe. They're coupled
  // (opening a section clears the focused page), so they're written together in
  // one updater to avoid a multi-param race; `push` gives back-swipe history.
  const [searchParams, setSearchParams] = useSearchParams();
  const section = parseSection(searchParams.get('section'));
  const focusedPagePublicId = searchParams.get('page');
  const [selectedPageId, setSelectedPageId] = useState<number | null>(null);
  const [editingCollection, setEditingCollection] = useState<WikiCollection | null>(null);
  const [collectionDialogOpen, setCollectionDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const qc = useQueryClient();
  const [expandedCollections, setExpandedCollections] = useState<Set<number>>(new Set());

  function setFocusedPagePublicId(pub: string | null) {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (pub) p.set('page', pub);
        else p.delete('page');
        return p;
      },
      { replace: false },
    );
  }

  function openSection(next: Section) {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        const s = serializeSection(next);
        if (s) p.set('section', s);
        else p.delete('section');
        p.delete('page'); // leaving the focused page
        return p;
      },
      { replace: false },
    );
    setSelectedPageId(null);
  }

  function toggleCollection(id: number) {
    setExpandedCollections((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  return (
    <div className="flex h-[calc(100vh-7rem)] gap-4">
      {/* Sidebar */}
      <aside className="bg-card flex w-72 shrink-0 flex-col gap-1 overflow-y-auto rounded-lg border p-2">
        <div className="flex items-center gap-2 px-2 py-1.5">
          <BookOpen className="text-primary size-5" />
          <h1 className="text-sm font-semibold">Wiki</h1>
        </div>

        <RailButton active={section.kind === 'home'} icon={Home} label="Home" onClick={() => openSection({ kind: 'home' })} />
        <RailButton active={section.kind === 'shared'} icon={Share2} label="Shared with me" onClick={() => openSection({ kind: 'shared' })} />
        <RailButton active={section.kind === 'private'} icon={Lock} label="Private" onClick={() => openSection({ kind: 'private' })} />
        <RailButton active={section.kind === 'archived'} icon={Archive} label="Archived" onClick={() => openSection({ kind: 'archived' })} />

        <div className="mt-2 flex items-center justify-between px-2 py-1">
          <span className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">Collections</span>
          {canCreateCollection ? (
            <div className="flex items-center gap-1">
              <button
                type="button"
                aria-label="Import from Confluence"
                title="Import from Confluence"
                onClick={() => setImportOpen(true)}
                className="text-muted-foreground hover:text-foreground rounded p-0.5"
              >
                <Import className="size-4" />
              </button>
              <button
                type="button"
                aria-label="New collection"
                onClick={() => {
                  setEditingCollection(null);
                  setCollectionDialogOpen(true);
                }}
                className="text-muted-foreground hover:text-foreground rounded p-0.5"
              >
                <FolderPlus className="size-4" />
              </button>
            </div>
          ) : null}
        </div>

        {collectionsQuery.isPending ? (
          <Skeleton className="mx-2 h-24" />
        ) : collections.length === 0 ? (
          <p className="text-muted-foreground px-2 py-2 text-xs">No collections yet.</p>
        ) : (
          collections.map((c, index) => (
            <CollectionRail
              key={c.id}
              collection={c}
              index={index}
              count={collections.length}
              canReorder={canCreateCollection}
              onMove={(dir) => moveCollection(index, dir)}
              expanded={expandedCollections.has(c.id)}
              active={section.kind === 'collection' && section.id === c.id}
              selectedPageId={section.kind === 'collection' && section.id === c.id ? selectedPageId : null}
              onToggle={() => toggleCollection(c.id)}
              onOpen={() => openSection({ kind: 'collection', id: c.id })}
              onSelectPage={(id) => {
                openSection({ kind: 'collection', id: c.id });
                setSelectedPageId(id);
              }}
              onSettings={() => {
                setEditingCollection(c);
                setCollectionDialogOpen(true);
              }}
            />
          ))
        )}
      </aside>

      {/* Main */}
      <main className="bg-card min-w-0 flex-1 overflow-y-auto rounded-lg border p-5">
        {focusedPagePublicId ? (
          <FocusedPage
            publicId={focusedPagePublicId}
            onBack={() => setFocusedPagePublicId(null)}
            onOpen={setFocusedPagePublicId}
          />
        ) : (
          <MainPane
            section={section}
            collections={collections}
            selectedPageId={selectedPageId}
            onSelectPage={setSelectedPageId}
            onOpenPageByPublicId={setFocusedPagePublicId}
          />
        )}
      </main>

      <CollectionDialog
        key={`${editingCollection?.id ?? 'new'}:${collectionDialogOpen}`}
        collection={editingCollection}
        open={collectionDialogOpen}
        onOpenChange={setCollectionDialogOpen}
        onCreated={(c) => openSection({ kind: 'collection', id: c.id })}
      />

      <ConfluenceImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        destination={{ kind: 'wiki' }}
        onImported={() => {
          qc.invalidateQueries({ queryKey: ['wiki-collections'] });
          qc.invalidateQueries({ queryKey: ['wiki-pages'] });
        }}
      />
    </div>
  );
}

function FocusedPage({
  publicId,
  onBack,
  onOpen,
}: {
  publicId: string;
  onBack: () => void;
  onOpen: (publicId: string) => void;
}) {
  const query = useWikiPage(publicId);
  return (
    <div className="space-y-3">
      <Button variant="ghost" size="sm" className="text-muted-foreground -ml-2" onClick={onBack}>
        <ChevronRight className="size-4 rotate-180" /> Back
      </Button>
      {query.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : query.data ? (
        <WikiPageEditor key={query.data.id} page={query.data} onAddChild={() => undefined} onOpenPage={onOpen} />
      ) : (
        <Empty>Page not found or no longer accessible.</Empty>
      )}
    </div>
  );
}

function RailButton({
  active,
  icon: Icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: typeof Home;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm',
        active ? 'bg-muted font-medium' : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
      )}
    >
      <Icon className="size-4" />
      {label}
    </button>
  );
}

function CollectionRail({
  collection,
  index,
  count,
  canReorder,
  onMove,
  expanded,
  active,
  selectedPageId,
  onToggle,
  onOpen,
  onSelectPage,
  onSettings,
}: {
  collection: WikiCollection;
  index: number;
  count: number;
  canReorder: boolean;
  onMove: (dir: 'up' | 'down') => void;
  expanded: boolean;
  active: boolean;
  selectedPageId: number | null;
  onToggle: () => void;
  onOpen: () => void;
  onSelectPage: (id: number) => void;
  onSettings: () => void;
}) {
  const pagesQuery = pagesHooks.useList(
    { collection: collection.id, is_archived: false, page_size: 1000 },
    expanded,
  );
  const create = pagesHooks.useCreate();
  const pages = rows<WikiPage>(pagesQuery.data);
  const canManage = collection.my_role === 'owner';
  const canEdit = collection.my_role === 'owner' || collection.my_role === 'editor';

  return (
    <div>
      <div
        className={cn(
          'group flex items-center gap-0.5 rounded pr-1',
          active ? 'bg-muted' : 'hover:bg-muted/50',
        )}
      >
        <button
          type="button"
          aria-label={expanded ? 'Collapse' : 'Expand'}
          onClick={onToggle}
          className="text-muted-foreground hover:text-foreground shrink-0 rounded p-0.5"
        >
          {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
        </button>
        <button
          type="button"
          // Clicking the row opens the collection and expands its tree; the
          // chevron stays as the explicit toggle (so re-clicking never collapses).
          onClick={() => {
            onOpen();
            if (!expanded) onToggle();
          }}
          className="flex min-w-0 flex-1 items-center gap-1.5 py-1 text-left text-sm"
        >
          <span className="text-sm">{collection.icon || '📁'}</span>
          <span className="flex-1 truncate">{collection.name}</span>
          {collection.visibility === 'private' ? (
            <Lock className="text-muted-foreground size-3 shrink-0" aria-label="Private" />
          ) : null}
        </button>
        {canReorder ? (
          <div className="flex shrink-0 items-center opacity-0 group-hover:opacity-100">
            <button
              type="button"
              aria-label="Move collection up"
              disabled={index === 0}
              onClick={() => onMove('up')}
              className="text-muted-foreground hover:text-foreground rounded p-0.5 disabled:opacity-30"
            >
              <ArrowUp className="size-3.5" />
            </button>
            <button
              type="button"
              aria-label="Move collection down"
              disabled={index === count - 1}
              onClick={() => onMove('down')}
              className="text-muted-foreground hover:text-foreground rounded p-0.5 disabled:opacity-30"
            >
              <ArrowDown className="size-3.5" />
            </button>
          </div>
        ) : null}
        {canManage ? (
          <button
            type="button"
            aria-label="Collection settings"
            onClick={onSettings}
            className="text-muted-foreground hover:text-foreground shrink-0 rounded p-1 opacity-0 group-hover:opacity-100"
          >
            <Settings2 className="size-3.5" />
          </button>
        ) : null}
      </div>

      {expanded ? (
        <div className="ml-3 border-l pl-1">
          {pagesQuery.isPending ? (
            <Skeleton className="my-1 h-12" />
          ) : (
            <>
              <WikiPageTree
                pages={pages.filter((p) => !p.is_archived)}
                allPages={pages}
                flat={false}
                canEdit={canEdit}
                selectedId={selectedPageId}
                onSelect={onSelectPage}
              />
              {canEdit ? (
                <button
                  type="button"
                  onClick={() =>
                    create.mutate(
                      { collection: collection.id, title: 'Untitled', visibility: 'public' },
                      { onSuccess: (p) => onSelectPage(p.id) },
                    )
                  }
                  className="text-muted-foreground hover:text-foreground flex w-full items-center gap-1.5 rounded px-2 py-1 text-xs"
                >
                  <Plus className="size-3.5" /> Add page
                </button>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main pane
// ---------------------------------------------------------------------------
function MainPane({
  section,
  collections,
  selectedPageId,
  onSelectPage,
  onOpenPageByPublicId,
}: {
  section: Section;
  collections: WikiCollection[];
  selectedPageId: number | null;
  onSelectPage: (id: number) => void;
  onOpenPageByPublicId: (pubId: string) => void;
}) {
  if (section.kind === 'home') return <HomeDashboard onOpenPage={onOpenPageByPublicId} />;
  if (section.kind === 'shared')
    return <FilteredPages params={{ shared: true, is_archived: false }} empty="Nothing shared with you yet." treeEditable={false} onOpenPage={onOpenPageByPublicId} />;
  if (section.kind === 'private')
    return <FilteredPages params={{ personal: true, is_archived: false }} empty="You have no personal pages." createPersonal onOpenPage={onOpenPageByPublicId} />;
  if (section.kind === 'archived')
    return <FilteredPages params={{ is_archived: true }} empty="Nothing archived." flat onOpenPage={onOpenPageByPublicId} />;
  // collection
  const collection = collections.find((c) => c.id === section.id);
  if (!collection) return <Empty>Collection not found.</Empty>;
  return (
    <CollectionPane
      collection={collection}
      selectedPageId={selectedPageId}
      onSelectPage={onSelectPage}
      onOpenPage={onOpenPageByPublicId}
    />
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="text-muted-foreground flex h-full items-center justify-center text-sm">{children}</div>;
}

function HomeDashboard({ onOpenPage }: { onOpenPage: (publicId: string) => void }) {
  const homeQuery = useWikiHome();
  const removeSticky = stickiesHooks.useRemove();

  if (homeQuery.isPending) return <Skeleton className="h-64 w-full" />;
  const recents: WikiRecent[] = homeQuery.data?.recents ?? [];
  const stickies: WikiSticky[] = homeQuery.data?.stickies ?? [];

  return (
    <div className="space-y-6">
      <section className="space-y-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Pin className="size-4" /> Pinned
        </h2>
        {stickies.length === 0 ? (
          <p className="text-muted-foreground text-sm">No pinned pages. Pin pages from their ⋯ menu.</p>
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {stickies.map((s) => (
              <Card key={s.id} className="group relative">
                <CardContent className="flex items-center gap-2 p-3 text-sm">
                  <button
                    type="button"
                    onClick={() => s.page_public_id && onOpenPage(s.page_public_id)}
                    className="flex min-w-0 flex-1 items-center gap-2 text-left"
                  >
                    <span className="text-lg">{s.page_icon || '📄'}</span>
                    <span className="flex-1 truncate">{s.page_title}</span>
                  </button>
                  <button
                    type="button"
                    aria-label="Unpin"
                    onClick={() => removeSticky.mutate(s.id)}
                    className="text-muted-foreground hover:text-foreground rounded p-0.5 opacity-0 group-hover:opacity-100"
                  >
                    <X className="size-3.5" />
                  </button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Clock className="size-4" /> Recently viewed
        </h2>
        {recents.length === 0 ? (
          <p className="text-muted-foreground text-sm">No recent pages yet.</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {recents.map((r) => (
              <li key={r.page}>
                <button
                  type="button"
                  onClick={() => onOpenPage(r.public_id)}
                  className="hover:bg-muted/50 flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
                >
                  <span className="text-base">{r.icon || '📄'}</span>
                  <span className="flex-1 truncate">{r.title}</span>
                  <span className="text-muted-foreground text-xs">
                    {new Date(r.viewed_at).toLocaleDateString()}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/** A flat/tree list of pages matching a filter, with the editor when one is selected. */
function FilteredPages({
  params,
  empty,
  flat = false,
  createPersonal = false,
  treeEditable = true,
  onOpenPage,
}: {
  params: Record<string, unknown>;
  empty: string;
  flat?: boolean;
  createPersonal?: boolean;
  treeEditable?: boolean;
  onOpenPage: (publicId: string) => void;
}) {
  const query = pagesHooks.useList({ ...params, page_size: 1000 });
  const create = pagesHooks.useCreate();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const pages = rows<WikiPage>(query.data);

  const effectiveId =
    selectedId != null && pages.some((p) => p.id === selectedId) ? selectedId : pages[0]?.id ?? null;
  const selected = pages.find((p) => p.id === effectiveId) ?? null;
  // Collapsed by default — the toggle stays visible so the list/create is one click away.
  const [treeCollapsed, setTreeCollapsed] = useState(true);

  return (
    <div className="flex h-full gap-4">
      <Button
        variant="ghost"
        size="icon"
        className="size-7 shrink-0"
        aria-label={treeCollapsed ? 'Show page list' : 'Hide page list'}
        title={treeCollapsed ? 'Show page list' : 'Hide page list'}
        onClick={() => setTreeCollapsed((v) => !v)}
      >
        {treeCollapsed ? <PanelLeftOpen className="size-4" /> : <PanelLeftClose className="size-4" />}
      </Button>
      {!treeCollapsed ? (
        <div className="w-60 shrink-0 space-y-2 border-r pr-3">
          {createPersonal ? (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() =>
                create.mutate(
                  { title: 'Untitled', visibility: 'private' },
                  { onSuccess: (p) => setSelectedId(p.id) },
                )
              }
            >
              <Plus className="size-4" /> New private page
            </Button>
          ) : null}
          {query.isPending ? (
            <Skeleton className="h-40 w-full" />
          ) : pages.length === 0 ? (
            <p className="text-muted-foreground px-1 py-4 text-sm">{empty}</p>
          ) : (
            <WikiPageTree
              pages={pages}
              allPages={pages}
              flat={flat}
              canEdit={treeEditable}
              selectedId={effectiveId}
              onSelect={setSelectedId}
            />
          )}
        </div>
      ) : null}
      <div className="min-w-0 flex-1">
        {selected ? (
          <WikiPageEditor key={selected.id} page={selected} allPages={pages} onAddChild={() => undefined} onOpenPage={onOpenPage} />
        ) : (
          <Empty>Select a page.</Empty>
        )}
      </div>
    </div>
  );
}

function CollectionPane({
  collection,
  selectedPageId,
  onSelectPage,
  onOpenPage,
}: {
  collection: WikiCollection;
  selectedPageId: number | null;
  onSelectPage: (id: number) => void;
  onOpenPage: (publicId: string) => void;
}) {
  const query = pagesHooks.useList({ collection: collection.id, is_archived: false, page_size: 1000 });
  const create = pagesHooks.useCreate();
  const archiveCollection = useCollectionAction(
    (id) => collectionsApi.archive(id),
    'Collection archived',
  );
  const syncCollection = useSyncCollection();
  const pages = rows<WikiPage>(query.data);
  const canEdit = collection.my_role === 'owner' || collection.my_role === 'editor';
  const canManage = collection.my_role === 'owner';
  // The page list duplicates the left sidebar tree, so it starts collapsed.
  const [treeCollapsed, setTreeCollapsed] = useState(true);
  const updateCollection = collectionsHooks.useUpdate();
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(collection.name);

  const commitName = () => {
    const next = nameDraft.trim();
    if (next && next !== collection.name) {
      updateCollection.mutate({ id: collection.public_id, input: { name: next } });
    } else {
      setNameDraft(collection.name);
    }
    setRenaming(false);
  };

  // Default to the FIRST root page (matches the tree's first item). `pages[0]`
  // is the first by global (order, title) — imported children all start at
  // order 0, so an alphabetically-early child would wrongly win.
  const ids = new Set(pages.map((p) => p.id));
  const firstRootId = pages.find((p) => p.parent == null || !ids.has(p.parent))?.id ?? null;
  const effectiveId =
    selectedPageId != null && pages.some((p) => p.id === selectedPageId)
      ? selectedPageId
      : firstRootId;
  const selected = pages.find((p) => p.id === effectiveId) ?? null;

  return (
    <div className="flex h-full flex-col gap-3">
      <div className="flex items-center gap-2 border-b pb-3">
        <Button
          variant="ghost"
          size="icon"
          className="size-7 shrink-0"
          aria-label={treeCollapsed ? 'Show page list' : 'Hide page list'}
          title={treeCollapsed ? 'Show page list' : 'Hide page list'}
          onClick={() => setTreeCollapsed((v) => !v)}
        >
          {treeCollapsed ? <PanelLeftOpen className="size-4" /> : <PanelLeftClose className="size-4" />}
        </Button>
        <span className="text-xl">{collection.icon || '📁'}</span>
        {renaming ? (
          <Input
            ref={(el) => el?.focus()}
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitName();
              else if (e.key === 'Escape') {
                setNameDraft(collection.name);
                setRenaming(false);
              }
            }}
            className="h-8 max-w-xs text-lg font-semibold"
          />
        ) : (
          <h2
            className="text-lg font-semibold"
            onDoubleClick={canManage ? () => { setNameDraft(collection.name); setRenaming(true); } : undefined}
            title={canManage ? 'Double-click to rename' : undefined}
          >
            {collection.name}
          </h2>
        )}
        {collection.visibility === 'private' ? (
          <span className="text-muted-foreground inline-flex items-center gap-1 text-xs">
            <Lock className="size-3" /> Private · <Users className="size-3" /> {collection.member_count}
          </span>
        ) : null}
        {canEdit ? (
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground ml-auto"
            title="Sync every page in this collection to the Knowledge Base"
            disabled={syncCollection.isPending}
            onClick={() => syncCollection.mutate(collection.id)}
          >
            <Database className="size-4" /> Sync to Knowledge Base
          </Button>
        ) : null}
        {canManage ? (
          <Button
            variant="ghost"
            size="sm"
            className={cn('text-muted-foreground', canEdit ? '' : 'ml-auto')}
            onClick={() => archiveCollection.mutate(collection.public_id)}
          >
            <Archive className="size-4" /> Archive collection
          </Button>
        ) : null}
      </div>

      <div className="flex min-h-0 flex-1 gap-4">
        {!treeCollapsed ? (
          <div className="w-60 shrink-0 space-y-2 border-r pr-3">
            {canEdit ? (
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() =>
                  create.mutate(
                    { collection: collection.id, title: 'Untitled', visibility: 'public' },
                    { onSuccess: (p) => onSelectPage(p.id) },
                  )
                }
              >
                <Plus className="size-4" /> Add page
              </Button>
            ) : null}
            {query.isPending ? (
              <Skeleton className="h-40 w-full" />
            ) : pages.length === 0 ? (
              <p className="text-muted-foreground px-1 py-4 text-sm">No pages yet.</p>
            ) : (
              <WikiPageTree
                pages={pages.filter((p) => !p.is_archived)}
                allPages={pages}
                flat={false}
                canEdit={canEdit}
                selectedId={effectiveId}
                onSelect={onSelectPage}
              />
            )}
          </div>
        ) : null}
        <div className="min-w-0 flex-1 overflow-y-auto">
          {selected ? (
            <WikiPageEditor
              key={selected.id}
              page={selected}
              allPages={pages}
              onOpenPage={onOpenPage}
              onAddChild={(parentId) =>
                create.mutate(
                  { collection: collection.id, parent: parentId, title: 'Untitled', visibility: 'public' },
                  { onSuccess: (p) => onSelectPage(p.id) },
                )
              }
            />
          ) : (
            <Empty>Select or create a page.</Empty>
          )}
        </div>
      </div>
    </div>
  );
}
